import { useState, useCallback, useEffect, useRef } from "react";
import { MantineProvider, Container, Box } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { ChatContainer, Header, ResumePage } from "./components";
import { INTERVIEW_STATIC_TEXTS } from "./constants/static-translations";
import { AnalyticsEvents } from "./constants/analytics-events";
import { useLocationHeaderInfo, useTranslation } from "./hooks";
import { usePageViewAnalytics, useTrackAnalytics } from "./hooks/useAnalytics";
import { SegmentContextProvider } from "./context/segment.context";
import { LocalModelBenchmarkRoute } from "@/routes/local-model-benchmark-route";
import { theme } from "./styles/theme";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "./styles/global.css";

interface InterviewPageContentProps {
  locationId: string;
  candidateId?: string;
}

function InterviewPageContent({
  locationId,
  candidateId,
}: InterviewPageContentProps) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [candidateEmail, setCandidateEmail] = useState<string | null>(null);
  const [languageCode, setLanguageCode] = useState<string | null>(null);
  // AS-1851: fetched once on mount so the header can show real values instead
  // of the legacy "Your Company" / "AI Interview" placeholders. Also feeds
  // the Segment attribution props so every funnel event carries company /
  // screener / location — see gated provider mount below.
  const {
    companyName,
    jobTitle,
    locationName,
    isPending: isLocationPending,
  } = useLocationHeaderInfo(locationId);
  // AS-1564: candidate-facing progress fields lifted from ChatContainer so the
  // Header can render the progress bar. Undefined means "no data yet" (pre-T&C)
  // or "cleared on session reset".
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState<
    number | undefined
  >(undefined);
  const [totalQuestions, setTotalQuestions] = useState<number | undefined>(
    undefined,
  );
  const { t, preloadTranslations } = useTranslation(languageCode);

  const handleSessionReady = useCallback((id: string) => {
    setSessionId(id);
  }, []);

  const handleCandidateEmailReady = useCallback((email: string) => {
    setCandidateEmail(email);
  }, []);

  const handleLanguageReady = useCallback((language: string) => {
    setLanguageCode(language);
  }, []);

  const handleProgressUpdate = useCallback(
    (current: number | undefined, total: number | undefined) => {
      setCurrentQuestionNumber(current);
      setTotalQuestions(total);
    },
    [],
  );

  useEffect(() => {
    if (languageCode) {
      preloadTranslations(INTERVIEW_STATIC_TEXTS);
    }
  }, [languageCode, preloadTranslations]);

  // Attribution values feed the Segment defaults; they start null and get
  // populated by `useLocationHeaderInfo` (~1 API roundtrip after mount).
  // The provider stays mounted throughout — remounting it here would
  // remount ChatContainer / useInterview, killing the Pusher subscription
  // and any in-flight session state. The `InterviewPageTracking` child
  // below is gated on `!isLocationPending` — i.e. we wait for the fetch
  // to resolve one way or the other, then fire the funnel events with
  // whatever attribution came back (may be null for legacy locations
  // whose metadata is missing). Gating on the values being non-null
  // would silently drop the entire funnel for those sessions, which
  // regresses vs. the pre-AS-2004 behavior; Greptile + Cursor both
  // flagged that on the initial PR.
  const attributionReady = !isLocationPending;

  return (
    <SegmentContextProvider
      interviewLocationId={locationId}
      sessionId={sessionId}
      company={companyName}
      screener={jobTitle}
      location={locationName}
      screenerLocationId={locationId}
    >
      {attributionReady && (
        <InterviewPageTracking
          interviewLocationId={locationId}
          sessionId={sessionId}
        />
      )}
      <div className="interview-page">
        {sessionId && (
          <div
            data-testid="interview-session-id"
            data-session-id={sessionId}
            hidden
          />
        )}
        <Header
          company={companyName ?? "Your Company"}
          title={jobTitle ?? "AI Interview"}
          sessionId={sessionId ?? undefined}
          candidateEmail={candidateEmail ?? undefined}
          currentQuestionNumber={currentQuestionNumber}
          totalQuestions={totalQuestions}
          translate={t}
        />
        <ChatContainer
          interviewLocationId={locationId}
          candidateId={candidateId}
          company={companyName ?? undefined}
          onSessionReady={handleSessionReady}
          onCandidateEmailReady={handleCandidateEmailReady}
          onLanguageReady={handleLanguageReady}
          onProgressUpdate={handleProgressUpdate}
          translate={t}
        />
      </div>
    </SegmentContextProvider>
  );
}

/**
 * AS-1832: tracker child rendered under the SegmentContextProvider so
 * it can use the analytics hooks. Fires `Landed on Screener` once the
 * session id is available, matching the v2 event's session-ready boundary.
 */
function InterviewPageTracking({
  interviewLocationId,
  sessionId,
}: {
  interviewLocationId: string;
  sessionId: string | null;
}) {
  usePageViewAnalytics("Interview");
  const track = useTrackAnalytics();
  const hasTrackedLandedRef = useRef(false);

  // The tracker is already gated on attribution settling. Wait for the
  // session as well so Landed remains joinable to a screening, as it was in
  // v2, regardless of which request resolves first.
  useEffect(() => {
    if (!sessionId || hasTrackedLandedRef.current) {
      return;
    }

    hasTrackedLandedRef.current = true;
    track(AnalyticsEvents.LandedOnScreener, {
      interviewLocationId,
      sessionId,
    });
  }, [interviewLocationId, sessionId, track]);

  return null;
}

function InterviewPage() {
  const { locationId, candidateId } = useParams<{
    locationId: string;
    candidateId?: string;
  }>();

  if (!locationId) {
    return (
      <Container>
        <Box py="xl" ta="center">
          Invalid interview link. Please check the URL.
        </Box>
      </Container>
    );
  }

  return (
    <InterviewPageContent locationId={locationId} candidateId={candidateId} />
  );
}

function HomePage() {
  return (
    <Container size="sm" py="xl">
      <Box ta="center">
        <h1>Interviewer Frontend</h1>
        <p>
          Welcome to the AI Interview platform. Use a valid interview link to
          start your session.
        </p>
        <p style={{ color: "gray", fontSize: "0.875rem" }}>
          URL format: /interview/:locationId or
          /interview/:locationId/:candidateId
        </p>
      </Box>
    </Container>
  );
}

function App() {
  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Notifications position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/interview/:locationId/:candidateId?"
            element={<InterviewPage />}
          />
          <Route path="/resume/:resumeToken" element={<ResumePage />} />
          <Route
            path="/model-benchmark/*"
            element={<LocalModelBenchmarkRoute />}
          />
        </Routes>
      </BrowserRouter>
    </MantineProvider>
  );
}

export default App;
